// Medication label (ฉลากยา) printing — sized for an 80 × 60 mm THERMAL sticker,
// ONE label per page (page-break-after each), one label per medication line.
// Sibling of lib/assetLabel.js; same print-window approach.
//
// THERMAL = PURE BLACK ON WHITE. A direct-thermal head has no grey: a #64748b
// "muted" caption dithers into a speckled smear and a hairline vanishes. So
// every colour here is #000, rules are ≥ 0.35mm, and hierarchy is carried by
// size, weight and one inverted (white-on-black) band for วิธีใช้ — the line
// the patient must not misread.
//
// FIT: the text is free-form (staff edit วิธีใช้/ข้อบ่งชี้/คำเตือน before
// printing), so a long warning could push the footer off the sticker. Each
// label is shrunk step by step until its content fits the 60mm box — a
// smaller label beats a cut-off doctor name or dose. `fitLabels` runs in the
// print window before window.print(), and in the dialog's preview iframe, so
// the preview is what prints.

import { MED_CLINIC } from '../data/medicationLabels.js'

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

const FONT_FILES = [
  { weight: 400, file: 'IBMPlexSansThaiLooped-Regular.ttf' },
  { weight: 600, file: 'IBMPlexSansThaiLooped-SemiBold.ttf' },
]

/**
 * @typedef {object} MedLabel
 * @property {string} clinic      'คลินิกทันตกรรมสไมล์ซีซันส์ สาขา อารีย์'
 * @property {string} patient     full name
 * @property {string} hn
 * @property {string} drug        'Paracetamol (500mg)'
 * @property {string} [drugTh]    'พาราเซตามอล'
 * @property {string|number} qty  printed as "จำนวน N"
 * @property {string} [unit]      'เม็ด' / 'แคปซูล'
 * @property {string} sig         วิธีใช้ — 'รับประทานครั้งละ 1 เม็ด …'
 * @property {string} [indication]
 * @property {string} [warning]
 * @property {string} [doctor]    'ทพ.ธนิตย์ วนิชนพรัตน์ (ท.9514)'
 * @property {string} [date]      '16 ต.ค. 2026'
 */

function labelCell(l, base) {
  const qty = String(l.qty ?? '').trim()
  return `
  <div class="label">
    <div class="fit">
      <div class="head">
        <img class="mark" src="${base}/doc/mark-black.png" alt="">
        <div>
          <div class="clinic">${esc(l.clinic)}</div>
          <div class="contact">โทร ${esc(MED_CLINIC.phone)} · LINE ${esc(MED_CLINIC.line)}</div>
        </div>
      </div>
      <div class="patient"><span class="pname">${esc(l.patient)}</span><span class="hn">HN ${esc(l.hn)}</span></div>
      <div class="drugrow">
        <div class="drug">${esc(l.drug)}${l.drugTh ? `<span class="drugth">(${esc(l.drugTh)})</span>` : ''}</div>
        ${qty ? `<div class="qty">จำนวน ${esc(qty)}${l.unit ? ` ${esc(l.unit)}` : ''}</div>` : ''}
      </div>
      <div class="sig">${esc(l.sig)}</div>
      ${l.indication ? `<div class="txt"><b>ข้อบ่งชี้:</b> ${esc(l.indication)}</div>` : ''}
      ${l.warning ? `<div class="txt"><b>คำเตือน:</b> ${esc(l.warning)}</div>` : ''}
      <div class="txt"><b>วันหมดอายุ:</b> กรุณาดูที่ซองยา</div>
      <div class="foot">${l.doctor ? `<span class="nw"><b>ผู้สั่งยา:</b> ${esc(l.doctor)}</span>` : ''}${l.date ? `<span class="nw"><b>วันที่จ่ายยา:</b> ${esc(l.date)}</span>` : ''}</div>
    </div>
  </div>`
}

// Runs inside the label document (print window or preview iframe). Shrinks each
// label's root font-size — every size below is in em — until it fits.
const FIT_SCRIPT = `
var BASE_PT=9.2;
function fitLabels(){
  var ls=document.querySelectorAll('.label');
  for(var i=0;i<ls.length;i++){
    var box=ls[i], f=box.querySelector('.fit'), s=1;
    // Measure at NATURAL height: .fit is normally height:100% with the footer
    // pushed down by margin-top:auto, and in that state scrollHeight never
    // reflects how tall the content really is. Room = .fit's own box (label
    // minus padding) — not the label's clientHeight, which includes padding.
    f.style.fontSize=''; f.style.height='100%';
    var room=f.clientHeight, roomW=f.clientWidth;
    f.style.height='auto';
    while(s>0.6 && (f.offsetHeight>room+0.5 || f.scrollWidth>roomW+0.5)){
      s-=0.04; f.style.fontSize=(s*BASE_PT)+'pt';
    }
    f.style.height='';
  }
}`

/**
 * Full HTML document for a set of labels.
 *   autoPrint — print on load (the print window).
 *   preview   — exactly one 80×60 label, no screen padding/backdrop and no
 *               scrollbars, so an iframe sized 80mm × 60mm shows the whole
 *               label and nothing else.
 */
export function buildMedLabelHtml(labels, { autoPrint = false, preview = false, origin = '' } = {}) {
  const list = (Array.isArray(labels) ? labels : []).filter(Boolean)
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '')
  const fonts = FONT_FILES.map((f) =>
    `@font-face{font-family:'MedLabel';font-weight:${f.weight};font-style:normal;font-display:block;src:url('${base}/doc/fonts/${f.file}') format('truetype')}`,
  ).join('')
  const onload = autoPrint
    ? `(document.fonts?document.fonts.ready:Promise.resolve()).then(function(){fitLabels();setTimeout(function(){window.print()},200)})`
    : `(document.fonts?document.fonts.ready:Promise.resolve()).then(fitLabels)`

  return `<!doctype html><html><head><meta charset="utf-8"><title>ฉลากยา</title>
  <style>
    ${fonts}
    @page { size: 80mm 60mm; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 80mm; color: #000; background: #fff;
      font-family: 'MedLabel', 'Noto Sans Thai', Tahoma, sans-serif;
      -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .label {
      width: 80mm; height: 60mm; padding: 3mm 3.4mm; overflow: hidden; background: #fff;
      page-break-after: always; break-after: page;
    }
    .label:last-child { page-break-after: auto; break-after: auto; }
    .fit { font-size: 9.2pt; line-height: 1.38; height: 100%; display: flex; flex-direction: column; }
    b { font-weight: 600; }
    .head { display: flex; align-items: center; gap: 1.6mm; }
    /* Black-only smile mark (public/doc/mark-black.png, cut from wordmark.png):
       the colour logo would print as grey dither on a thermal head. */
    .mark { height: 2.3em; width: auto; flex: none; }
    .clinic { font-size: 0.82em; font-weight: 600; }
    .contact { font-size: 0.68em; }
    .patient {
      display: flex; justify-content: space-between; align-items: baseline; gap: 2mm;
      margin-top: 0.6mm; padding-bottom: 0.8mm; border-bottom: 0.45mm solid #000;
      font-size: 0.98em; font-weight: 600;
    }
    .pname { min-width: 0; overflow-wrap: anywhere; }
    .hn { flex: none; white-space: nowrap; }
    .drugrow { display: flex; justify-content: space-between; align-items: baseline; gap: 2mm; margin-top: 1mm; }
    .drug { font-size: 1.3em; font-weight: 600; line-height: 1.2; min-width: 0; overflow-wrap: anywhere; }
    /* Own line, never broken: inline it split mid-word ("อะม็ / อกซีซิลลิน"). */
    .drugth { display: block; white-space: nowrap; font-size: 0.62em; font-weight: 400; }
    .qty { flex: none; white-space: nowrap; font-size: 0.86em; font-weight: 600;
      border: 0.35mm solid #000; padding: 0 1.2mm; border-radius: 0.8mm; }
    .sig {
      margin-top: 1.2mm; padding: 1mm 1.8mm; background: #000; color: #fff;
      font-size: 0.98em; font-weight: 600; line-height: 1.32; border-radius: 0.6mm;
    }
    .txt { font-size: 0.74em; margin-top: 0.7mm; }
    .foot { margin-top: auto; padding-top: 0.8mm; border-top: 0.35mm solid #000; font-size: 0.68em;
      display: flex; flex-wrap: wrap; column-gap: 2.4mm; }
    /* Doctor and date each wrap as a WHOLE — never "16 / ต.ค. 2026". */
    .nw { white-space: nowrap; }
    ${preview
    ? 'html, body { height: 60mm; overflow: hidden; }'
    : `@media screen {
      body { background: #e2e8f0; padding: 4mm; }
      .label { box-shadow: 0 1px 4px rgba(0,0,0,.25); margin-bottom: 3mm; }
    }`}
  </style>
  <script>${FIT_SCRIPT}</script>
  </head>
  <body onload="${onload.replace(/"/g, '&quot;')}">${list.map((l) => labelCell(l, base)).join('')}</body></html>`
}

/** Open the print window — one 80×60 page per label. */
export function openMedLabelPrint(labels) {
  const list = (Array.isArray(labels) ? labels : []).filter(Boolean)
  if (!list.length) return false
  const w = window.open('', '_blank', 'width=480,height=420')
  if (!w) return false
  w.document.write(buildMedLabelHtml(list, { autoPrint: true }))
  w.document.close()
  return true
}
